import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Dumbbell, ShieldAlert, MessageCircle, Calendar as CalendarIcon, Bell, Sparkles, ScanLine, KeyRound } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import ProgressCard from "../components/ProgressCard";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberPortal() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/public/member/${encodeURIComponent(code)}`)
      .then((r) => setData(r.data))
      .catch((err) => setError(err?.response?.data?.detail || "Could not load portal"));
  }, [code]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="max-w-md border border-border rounded-2xl p-8 bg-card text-center" data-testid="portal-error">
          <div className="w-14 h-14 rounded-full bg-rose-100 dark:bg-rose-950/40 flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-6 h-6 text-rose-600" />
          </div>
          <h1 className="font-display text-xl font-bold text-foreground mb-1">Link looks invalid</h1>
          <p className="text-sm text-muted-foreground">{error}. Ask your gym for a new portal link.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="w-10 h-10 rounded-full border-2 border-muted border-t-primary animate-spin" />
      </div>
    );
  }

  const { member, gym, stats } = data;
  const upcomingEvents = data.upcoming_events || [];
  const upcomingBookings = data.upcoming_bookings || [];
  const updates = data.updates || [];
  const daysLeft = stats.days_left;
  const expires = member.expiry_date ? new Date(member.expiry_date) : null;
  const ownerPhone = member.phone ? member.phone.replace(/\D/g, "") : "";
  const renewText = encodeURIComponent(`Hi ${gym.name}, I'm ${member.name}. I'd like to renew my ${member.plan} membership. Please share payment options.`);
  const renewHref = ownerPhone ? `https://wa.me/${ownerPhone}?text=${renewText}` : null;

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <header className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-indigo-500/5" aria-hidden />
        <div className="max-w-3xl mx-auto px-5 py-12 relative">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <Dumbbell className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Fitnudge portal</p>
              <p className="font-display text-base font-bold text-foreground" data-testid="portal-gym-name">{gym.name}</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-1">Welcome back,</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-foreground" data-testid="portal-member-name">
            {member.name}
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-md">
            Your progress, streak and membership - all in one place. Sign in to the gym by scanning your QR or entering your PIN.
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 space-y-6">
        {/* Membership + Renew */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="portal-membership">
          <div className="md:col-span-2 border border-border rounded-xl p-5 bg-card">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Your plan</p>
            <p className="font-display text-2xl font-bold text-foreground mb-1">{member.plan}</p>
            {expires && (
              <p className="text-sm text-muted-foreground">
                Expires <span className="text-foreground font-medium">{expires.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</span>
                {daysLeft !== null && daysLeft >= 0 && <> · <span className="font-medium text-foreground">{daysLeft} day{daysLeft === 1 ? "" : "s"} left</span></>}
                {daysLeft !== null && daysLeft < 0 && <> · <span className="font-medium text-rose-600">expired {-daysLeft}d ago</span></>}
              </p>
            )}
          </div>
          <div className="border border-border rounded-xl p-5 bg-card flex flex-col items-start justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Need to renew?</p>
            {renewHref ? (
              <a
                href={renewHref}
                target="_blank"
                rel="noreferrer"
                data-testid="portal-renew-btn"
                className="mt-2 inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1EBE5D] text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors w-full justify-center"
              >
                <MessageCircle className="w-4 h-4" /> Message {gym.name}
              </a>
            ) : (
              <span className="text-xs text-muted-foreground mt-2">Contact your gym to renew.</span>
            )}
          </div>
        </div>

        {/* Quick check-in: QR + PIN */}
        {(member.attendance_code || member.pin) && (
          <div className="border border-border rounded-xl p-5 bg-card" data-testid="portal-checkin-card">
            <h3 className="font-display text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <ScanLine className="w-4 h-4 text-primary" /> Check in at the gym
            </h3>
            <div className="flex flex-col sm:flex-row gap-5 items-center">
              {member.attendance_code && (
                <div className="flex flex-col items-center gap-2" data-testid="portal-qr">
                  <div className="bg-white p-3 rounded-lg border border-border">
                    <QRCodeCanvas value={`gympulse:${member.attendance_code}`} size={120} level="M" includeMargin={false} />
                  </div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/80 font-semibold">Scan at kiosk</p>
                </div>
              )}
              {member.pin && (
                <div className="flex-1 w-full text-center sm:text-left">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/80 font-semibold mb-2">Or enter PIN</p>
                  <div className="font-display text-3xl font-bold tracking-[0.3em] text-foreground tabular-nums" data-testid="portal-pin">
                    {member.pin}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1">
                    <KeyRound className="w-3 h-3" /> 4-digit PIN · keep it private
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Progress */}
        <ProgressCard portalStats={stats} compact />

        {/* Your upcoming classes */}
        {upcomingBookings.length > 0 && (
          <div className="border border-border rounded-xl p-5 bg-card" data-testid="portal-upcoming-bookings">
            <h3 className="font-display text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-primary" /> Your upcoming classes
            </h3>
            <ul className="space-y-2">
              {upcomingBookings.slice(0, 6).map((b, i) => (
                <li
                  key={b.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-primary/5 border border-primary/10"
                  data-testid={`portal-booking-${i}`}
                >
                  <span className="w-2 h-2 rounded-full bg-primary" aria-hidden />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">{b.class_name || "Class"}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(b.instance_date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                      {b.start_time ? ` · ${b.start_time}` : ""}
                      {b.duration_min ? ` · ${b.duration_min} min` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Mini calendar - current month with check-in dots */}
        <PortalMiniCalendar checkins={stats.recent_checkins || []} bookings={upcomingBookings} />

        {/* Upcoming gym events */}
        {upcomingEvents.length > 0 && (
          <div className="border border-border rounded-xl p-5 bg-card" data-testid="portal-upcoming-events">
            <h3 className="font-display text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-indigo-500" /> Upcoming events
            </h3>
            <ul className="space-y-2">
              {upcomingEvents.map((ev, i) => (
                <li
                  key={ev.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border"
                  data-testid={`portal-event-${i}`}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: ev.color || "#6366F1" }}
                    aria-hidden
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">{ev.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(ev.start_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                      {" · "}
                      {new Date(ev.start_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      {" · "}
                      <span className="capitalize">{ev.kind}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Updates feed */}
        {updates.length > 0 && (
          <div className="border border-border rounded-xl p-5 bg-card" data-testid="portal-updates">
            <h3 className="font-display text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Bell className="w-4 h-4 text-amber-500" /> Updates from {gym.name}
            </h3>
            <ul className="space-y-3">
              {updates.slice(0, 5).map((u, i) => (
                <li key={i} className="text-sm" data-testid={`portal-update-${i}`}>
                  <p className="text-foreground/85 leading-relaxed">{u.text}</p>
                  <p className="text-[11px] text-muted-foreground/70 mt-0.5">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}
                    {u.kind ? ` · ${u.kind.replace(/_/g, " ")}` : ""}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recent check-ins */}
        <div className="border border-border rounded-xl p-5 bg-card" data-testid="portal-recent">
          <h3 className="font-display text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-muted-foreground" /> Recent check-ins
          </h3>
          {stats.recent_checkins.length === 0 ? (
            <p className="text-sm text-muted-foreground/70">No check-ins yet - your first session unlocks the streak 🌱</p>
          ) : (
            <ol className="space-y-2">
              {stats.recent_checkins.map((c, i) => (
                <li key={i} className="flex items-center gap-3 text-sm" data-testid={`portal-recent-${i}`}>
                  <span className={`w-2 h-2 rounded-full ${c.method === "qr" ? "bg-blue-500" : c.method === "pin" ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
                  <span className="text-foreground">
                    {new Date(c.checked_in_at).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                  </span>
                  <span className="text-muted-foreground ml-auto">
                    {new Date(c.checked_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 ml-2">
                    {c.method}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <footer className="text-center text-xs text-muted-foreground/70 py-6">
          Powered by <Link to="/" className="font-semibold hover:text-foreground">Fitnudge</Link> · This is your personal portal.
        </footer>
      </main>
    </div>
  );
}

const DAY_LETTERS = ["M", "T", "W", "T", "F", "S", "S"];

function PortalMiniCalendar({ checkins = [], bookings = [] }) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0).getDate();
  const firstDow = (firstOfMonth.getDay() + 6) % 7; // Mon=0

  // Build sets of "yyyy-mm-dd" keys for check-in days and booking days
  const checkinDays = new Set();
  checkins.forEach(c => {
    if (c?.checked_in_at) checkinDays.add(c.checked_in_at.slice(0, 10));
  });
  const bookingDays = new Set();
  bookings.forEach(b => { if (b?.instance_date) bookingDays.add(b.instance_date); });

  const todayKey = now.toISOString().slice(0, 10);

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="border border-border rounded-xl p-5 bg-card" data-testid="portal-mini-calendar">
      <h3 className="font-display text-base font-semibold text-foreground mb-3 flex items-center gap-2">
        <CalendarIcon className="w-4 h-4 text-muted-foreground" />
        {firstOfMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
      </h3>
      <div className="grid grid-cols-7 text-[10px] font-semibold uppercase text-muted-foreground/70 mb-1.5">
        {DAY_LETTERS.map((l, i) => <div key={i} className="text-center">{l}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, idx) => {
          if (!d) return <div key={idx} className="h-9" />;
          const k = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const hasCheck = checkinDays.has(k);
          const hasBooking = bookingDays.has(k);
          const isToday = k === todayKey;
          return (
            <div
              key={idx}
              className={`relative h-9 flex items-center justify-center text-xs rounded-md transition-colors ${
                isToday
                  ? "bg-primary/15 text-primary font-bold ring-1 ring-primary/30"
                  : hasCheck
                    ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 font-semibold"
                    : "text-foreground/80"
              }`}
            >
              {d}
              {hasBooking && !hasCheck && (
                <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" aria-hidden />
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-3 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded bg-emerald-500/40" /> Checked in
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-primary" /> Booked class
        </span>
      </div>
    </div>
  );
}

